import { ApiError } from "./errors";
import type { ProblemDetail } from "./generated/model";

/**
 * Fetch-based Orval mutator (ADR-0020) — every generated operation funnels through here.
 * Base URL comes from the SPA env (VITE_API_URL — an origin like https://api.example.com);
 * generated paths already embed the full `/api/v1/...` prefix, so the same-origin fallback
 * is "".
 *
 * Fetch resolves on HTTP errors, so non-2xx THROWS a typed ApiError carrying the parsed
 * RFC 9457 ProblemDetail (ADR-0008) — TanStack Query's isError/retry semantics stay identical
 * to the axios era. A token-provider seam lets @repo/auth attach the bearer token without
 * this package depending on an auth SDK.
 */
const BASE_URL: string = import.meta.env?.VITE_API_URL ?? "";

/** Default guard for calls that arrive without a TanStack Query abort signal. */
const DEFAULT_TIMEOUT_MS = 30_000;

type TokenProvider = () => string | undefined | Promise<string | undefined>;

let getToken: TokenProvider = () => undefined;

/**
 * Allows @repo/auth to wire the access-token accessor without api-client depending on it.
 */
export function setAuthTokenProvider(provider: TokenProvider): void {
  getToken = provider;
}

type ImpersonationSessionProvider = () => string | undefined;

let getImpersonationSession: ImpersonationSessionProvider = () => undefined;

/**
 * Operator impersonation (super-admin.md): when an operator has stepped into a venue, the SPA stamps
 * the active `X-Impersonation-Session` id on every request so the backend runs it under the target
 * tenant (attributed to the operator). The provider is wired by the operator surface without this
 * package depending on it; unset (the normal staff path) it is a no-op.
 */
export function setImpersonationSessionProvider(provider: ImpersonationSessionProvider): void {
  getImpersonationSession = provider;
}

/** The PostHog person + session ids, so the backend can link its logs to the replay + person. */
type PostHogContext = { distinctId?: string; sessionId?: string };
type PostHogContextProvider = () => PostHogContext;

let getPostHogContext: PostHogContextProvider = () => ({});

/**
 * Lets the app forward the current PostHog session/distinct id (from `@repo/analytics`, never
 * `posthog-js` directly) so backend wide-event logs link to the user's Session Replay and person —
 * including anonymous public booking traffic where there's no bearer token. A no-op until wired.
 */
export function setPostHogContextProvider(provider: PostHogContextProvider): void {
  getPostHogContext = provider;
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Build the outbound headers (auth, impersonation, PostHog, idempotency) shared by every request. */
async function buildHeaders(init: RequestInit): Promise<Headers> {
  const headers = new Headers(init.headers);
  const token = await getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const impersonationSession = getImpersonationSession();
  if (impersonationSession && !headers.has("X-Impersonation-Session")) {
    headers.set("X-Impersonation-Session", impersonationSession);
  }
  // Link backend logs to the PostHog person + Session Replay (server reads these in LogContextFilter).
  const posthog = getPostHogContext();
  if (posthog.distinctId && !headers.has("X-PostHog-Distinct-Id")) {
    headers.set("X-PostHog-Distinct-Id", posthog.distinctId);
  }
  if (posthog.sessionId && !headers.has("X-PostHog-Session-Id")) {
    headers.set("X-PostHog-Session-Id", posthog.sessionId);
  }
  // Idempotent writes (ADR-0025): stamp a key on unsafe methods when the caller didn't.
  // Honest limitation: each call mints a FRESH key, so automatic retries of the same logical
  // mutation get different keys — for true retry-safety pass an explicit Idempotency-Key.
  if (UNSAFE_METHODS.has((init.method ?? "GET").toUpperCase()) && !headers.has("Idempotency-Key")) {
    headers.set("Idempotency-Key", crypto.randomUUID());
  }
  return headers;
}

function timeoutSignal(init: RequestInit): AbortSignal {
  return init.signal instanceof AbortSignal
    ? AbortSignal.any([init.signal, AbortSignal.timeout(DEFAULT_TIMEOUT_MS)])
    : AbortSignal.timeout(DEFAULT_TIMEOUT_MS);
}

export const customInstance = async <T>(url: string, init: RequestInit): Promise<T> => {
  const headers = await buildHeaders(init);
  const response = await fetch(`${BASE_URL}${url}`, {
    ...init,
    headers,
    signal: timeoutSignal(init),
  });

  const contentType = response.headers.get("content-type") ?? "";
  const hasJsonBody = response.status !== 204 && contentType.includes("json");

  if (!response.ok) {
    const problem = hasJsonBody
      ? await response.json().then(
          (body: unknown) => body as ProblemDetail,
          () => undefined, // malformed error body: still throw, just without a problem
        )
      : undefined;
    throw new ApiError(response.status, problem);
  }

  return (hasJsonBody ? await response.json() : undefined) as T;
};

export default customInstance;
